import React, { createContext, ReactNode } from 'react'

// Declares that a subtree is OUTSIDE the brand-portal URL space, so
// useNetworkScope reports an empty basePath there regardless of who is looking.
//
// Why this exists (TDC-1609): once brand roles resolve a basePath from their own
// membership, a logged-in brand user visiting a PUBLIC page — /orders/public/:token,
// /projects/share/:id, the standalone PDP audit — would have a non-empty
// basePath, and every shared component (VideoCard, ProfileHeaderBar, Breadcrumbs)
// would prefix its links on a page that has no portal chrome and no portal
// routes. The share link would silently break, but only for viewers who happen
// to be logged-in brand users, which is the kind of bug that reaches production.
//
// Declaring it at the route table is deliberate: the alternative is each
// component sniffing location.pathname for share-ness, which ProfileHeaderBar
// already hand-rolls and which has to be repeated (and kept correct) forever.
export const PortalScopeContext = createContext<{ basePath: string } | null>(null)

export function PortalScope({
  basePath = '',
  children,
}: {
  basePath?: string
  children: ReactNode
}) {
  // Stable identity per basePath so consumers don't re-render on every parent
  // render; there is only ever one value in practice ('').
  const value = React.useMemo(() => ({ basePath }), [basePath])
  return <PortalScopeContext.Provider value={value}>{children}</PortalScopeContext.Provider>
}
